import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import FreeCompetitorReport from '../components/FreeCompetitorReport';

const PLAN_IDS = [
  { id: 'starter', highlighted: false },
  { id: 'pro', highlighted: true },
];

const PLAN_PRICES = {
  starter: '10\u20AC',
  pro: '29,99\u20AC',
};

function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    { icon: '\u25C8', title: t('landing.featurePedidosTitle'), desc: t('landing.featurePedidosDesc') },
    { icon: '\u25C9', title: t('landing.featureProductosTitle'), desc: t('landing.featureProductosDesc') },
    { icon: '\uD83D\uDCC8', title: t('landing.featureTendenciasTitle'), desc: t('landing.featureTendenciasDesc') },
    { icon: '\u26A1', title: t('landing.featureAutomatizacionesTitle'), desc: t('landing.featureAutomatizacionesDesc') },
  ];

  const planFeatures = {
    starter: [t('landing.planStarterFeature1'), t('landing.planFeatureCommon'), t('landing.planStarterFeature3')],
    pro: [t('landing.planProFeature1'), t('landing.planFeatureCommon'), t('landing.planProFeature3')],
  };

  const planNames = { starter: 'Starter', pro: 'Pro' };

  return (
    <div className="bg-panel min-h-screen font-sans text-white">
      <div className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <div>
          <h1 className="text-accent text-lg font-extrabold leading-tight">{t('common.appName')}</h1>
          <p className="text-muted text-[11px]">{t('common.tagline')}</p>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => navigate('/login')}
            className="bg-transparent border border-edge rounded-lg px-4 py-2 text-white text-sm cursor-pointer"
          >
            {t('common.login')}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto text-center px-6 pt-12 pb-16">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          {t('landing.heroTitle1')} <span className="text-accent">{t('landing.heroTitle2')}</span>
        </h2>
        <p className="text-muted text-base sm:text-lg mb-8">
          {t('landing.heroSubtitle')}
        </p>
        <button
          onClick={() => navigate('/register')}
          className="bg-accent text-panel font-bold text-sm px-6 py-3 rounded-lg cursor-pointer"
        >
          {t('landing.ctaStart')}
        </button>
      </div>

      <FreeCompetitorReport />

      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-surface border border-edge rounded-2xl p-5">
              <div className="bg-surfaceAlt rounded-lg w-9 h-9 flex items-center justify-center text-accent text-base mb-3">
                {f.icon}
              </div>
              <p className="text-white text-sm font-semibold mb-1.5">{f.title}</p>
              <p className="text-muted text-xs leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-20">
        <h3 className="text-white text-2xl font-bold text-center mb-8">{t('landing.plansTitle')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {PLAN_IDS.map((plan) => (
            <div
              key={plan.id}
              className={`bg-surface border rounded-2xl p-6 flex flex-col ${plan.highlighted ? 'border-accent' : 'border-edge'}`}
            >
              <h4 className="text-white text-lg font-bold mb-2">{planNames[plan.id]}</h4>
              <p className="text-white text-3xl font-bold tracking-tight mb-4">
                {PLAN_PRICES[plan.id]}
                <span className="text-muted text-sm font-normal">{t('landing.perMonth')}</span>
              </p>
              <ul className="flex flex-col gap-2 mb-6 flex-1">
                {planFeatures[plan.id].map((f) => (
                  <li key={f} className="text-muted text-sm flex items-start gap-2">
                    <span className="text-accent">{'\u2713'}</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(`/register?plan=${plan.id}`)}
                className="bg-accent text-panel font-bold text-sm px-4 py-2.5 rounded-lg cursor-pointer"
              >
                {t('landing.startWithPlan', { plan: planNames[plan.id] })}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-edge">
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted">
          <span>{'\u00A9'} {new Date().getFullYear()} Flash Multiservice</span>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/aviso-legal')} className="bg-transparent border-0 text-muted hover:text-white cursor-pointer text-xs">{t('landing.footerLegal')}</button>
            <button onClick={() => navigate('/privacidad')} className="bg-transparent border-0 text-muted hover:text-white cursor-pointer text-xs">{t('landing.footerPrivacy')}</button>
            <button onClick={() => navigate('/cookies')} className="bg-transparent border-0 text-muted hover:text-white cursor-pointer text-xs">{t('landing.footerCookies')}</button>
            <button onClick={() => navigate('/developers')} className="bg-transparent border-0 text-muted hover:text-white cursor-pointer text-xs">{t('landing.footerDevelopers')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Landing;